import DOMPurify from 'dompurify'

/**
 * Конфигурация DOMPurify для безопасного отображения HTML
 */
const sanitizeConfig = {
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'span', 'div',
    'ul', 'ol', 'li',
    'strong', 'em', 'b', 'i', 'u', 's',
    'a', 'img',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'blockquote', 'code', 'pre',
    'hr',
    // Swing JLabel HTML subset
    'font', 'body', 'center', 'small',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel',
    'src', 'alt', 'width', 'height',
    'style', 'class', 'id',
    'colspan', 'rowspan',
    'color', 'face', 'size', 'align',
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
}

/** `type=HTML` field preview without `<script>` (WUI-521 mode A). */
const platformFieldHtmlSanitizeConfig = {
  ...sanitizeConfig,
  ALLOWED_TAGS: [
    ...sanitizeConfig.ALLOWED_TAGS,
    'style',
    'label',
    'form',
    'input',
    'button',
  ],
  ALLOWED_ATTR: [
    ...sanitizeConfig.ALLOWED_ATTR,
    'hidden',
    'role',
    'required',
    'border',
    'cellspacing',
    'cellpadding',
    'action',
    'method',
    'type',
    'name',
    'value',
    'title',
    'aria-label',
    'aria-modal',
    'aria-labelledby',
    'aria-hidden',
  ],
  ADD_ATTR: ['hidden'],
  // Do not set KEEP_CONTENT: false. DOMPurify wraps fragments in `<html>`;
  // that tag is not allowed, so KEEP_CONTENT:false drops the whole tree (form 2808).
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
}

/** Metadata element `style` wrapper: trusted platform markup may include `<style>`. */
const elementStyleWrapperSanitizeConfig = {
  ...sanitizeConfig,
  ALLOWED_TAGS: [...sanitizeConfig.ALLOWED_TAGS, 'style'],
  FORBID_TAGS: sanitizeConfig.FORBID_TAGS.filter((tag) => tag !== 'style'),
}

/**
 * Санитизация HTML обёртки из поля `<style>` элемента метаданных (ElementStyleWrapper).
 */
export function sanitizeElementStyleWrapperHtml(html: string): string {
  if (!html || typeof html !== 'string') return ''

  try {
    return DOMPurify.sanitize(html, elementStyleWrapperSanitizeConfig)
  } catch (error) {
    console.error('HTML sanitization error:', error)
    return html.replace(/<[^>]*>/g, '')
  }
}

/**
 * Санитизация HTML строки
 */
export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== 'string') return ''

  try {
    return DOMPurify.sanitize(html, sanitizeConfig)
  } catch (error) {
    console.error('HTML sanitization error:', error)
    return html.replace(/<[^>]*>/g, '')
  }
}

/**
 * Санитизация значения поля `type=HTML` для v-html (без исполнения script).
 */
export function sanitizePlatformFieldHtml(html: string): string {
  if (!html || typeof html !== 'string') return ''

  try {
    return DOMPurify.sanitize(html, platformFieldHtmlSanitizeConfig)
  } catch (error) {
    console.error('HTML sanitization error:', error)
    return html.replace(/<[^>]*>/g, '')
  }
}

function unwrapHtmlDocument(html: string): string {
  const trimmed = html.trim()
  if (!trimmed) return ''

  const lower = trimmed.toLowerCase()
  if (!lower.startsWith('<html>') && !lower.startsWith('<html ')) {
    return trimmed
  }

  const htmlMatch = trimmed.match(/<html[^>]*>(.*?)<\/html>/is)
  if (htmlMatch?.[1]) return htmlMatch[1].trim()
  const openTagMatch = trimmed.match(/<html[^>]*>(.*)/is)
  return openTagMatch?.[1]?.trim() || trimmed
}

/**
 * HTML подписей / tiles fallback: снимает обёртку `<html>` (как Swing JLabel) и режет XSS.
 */
export function sanitizePlatformDisplayHtml(html: string): string {
  if (!html || typeof html !== 'string') return ''
  return sanitizeHtml(unwrapHtmlDocument(html))
}

/**
 * Проверка содержит ли строка HTML теги
 */
export function containsHtml(text: string): boolean {
  if (!text || typeof text !== 'string') return false
  return /<[a-z][\s\S]*>/i.test(text)
}
